import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Avatar,
  FilterOptionsState,
  MenuItem,
  Popper,
} from '@mui/material'
import { styled } from '@mui/system'
import InputBase from '@mui/material/InputBase'
import { Column, GridApi, RowNode } from 'ag-grid-community'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import repositories from '../../../../../../meta/repositories'
import { RowData } from '../../../../../../BulkSheet/RowDataManager/rowDataManager'
import objects from '../../../../../../../../utils/objects'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import { BackgroundColor } from '../../../../../../../../styles/commonStyles'

// Styles
const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
})
const StyledMenuItem = styled(MenuItem)({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
})
const MenuItemLabel = styled('span')({
  whiteSpace: 'nowrap',
})
const StyledAvatar = styled(Avatar)({
  marginRight: '5px',
  height: '23px',
  width: '23px',
  borderRadius: '50%',
})
const NoImageIcon = styled(PersonRoundedIcon)({
  marginLeft: '5px',
  marginRight: '5px',
  borderRadius: '50%',
  height: '23px',
  minWidth: '23px',
  backgroundColor: BackgroundColor.GREY,
})

const iconDefined = ['ProjectMember', 'User']

interface AutocompleteOption {
  uuid: string
  name: string
  iconUrl?: string
}

interface Props {
  value: AutocompleteOption
  node: RowNode
  api: GridApi
  gridApi: GridApi
  eventKey: string | null
  rowIndex: number
  column: Column
  data: RowData

  field: string
  uiMeta: FunctionProperty
  onValueChange: (value: any) => void
}

const AutocompleteCellEditor = (props: Props) => {
  const [value, setValue] = useState<AutocompleteOption | undefined>()
  const [text, setText] = useState(() =>
    props.eventKey?.length === 1 ? props.eventKey : ''
  )
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([])
  const [open, setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const lastPressedKeyRef = useRef('')
  const lastValueRef = useRef<AutocompleteOption | undefined>()
  const valueRef = useRef<AutocompleteOption | undefined>()

  const updateValue = (newValue: AutocompleteOption | undefined) => {
    valueRef.current = newValue
    setValue(newValue)
  }

  // debounce: ref 経由で最新の props/state にアクセス
  const setOptionsRef = useRef<(searchText: string) => Promise<void>>()
  setOptionsRef.current = async (searchText: string) => {
    const cellEditorParams = props.column.getColDef().cellEditorParams
    const searchOptions = props.uiMeta.searchOptions.build(props.data)
    let options: AutocompleteOption[] = await repositories[
      props.uiMeta.referenceEntity!
    ].search(searchText, searchOptions, props.uiMeta)
    const selectOptionsFilter = cellEditorParams.selectOptionsFilter
    options = selectOptionsFilter ? selectOptionsFilter(options) : options
    if (props.uiMeta.propertyType === 'SELECT') {
      options = options.filter(v => v.name.includes(searchText))
    }
    setSuggestions(options)
    setOpen(options.length > 0)
  }

  const search = useMemo(
    () => _.debounce((v: string) => setOptionsRef.current?.(v), 300),
    []
  )

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
    if (props.eventKey === 'Delete' || props.eventKey === 'Backspace') {
      props.api.clearFocusedCell()
      props.api.setFocusedCell(props.rowIndex, props.column)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。セルエディタの初期化処理
  }, [])

  useEffect(() => {
    const input = inputRef.current
    if (!input) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (open || text)) {
        event.preventDefault()
      }
    }
    input.addEventListener('keydown', handler)
    return () => input.removeEventListener('keydown', handler)
  }, [open, text])

  const handleFocus = () => {
    search(text ? text : '')
    // save previous value to recover when escape key is pressed.
    lastValueRef.current = objects.getValue(
      props.node.data,
      props.field || props.uiMeta.externalId
    )
  }

  const handleInputChange = (
    _event: React.ChangeEvent<{}>,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'input') {
      setText(newValue)
      search(newValue)
    }
  }

  const handleValueChanged = (
    event: React.ChangeEvent<{}>,
    newOption: AutocompleteOption | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'blur') return
    props.onValueChange(newOption || undefined)
    props.node.setDataValue(props.field || props.uiMeta.externalId, newOption)
    // @ts-ignore
    const keyCode = event.keyCode
    updateValue(newOption || undefined)
    setText(newOption ? newOption.name : '')

    if (lastPressedKeyRef.current !== 'Tab') {
      let nextRowIndex
      if (keyCode === 13) {
        nextRowIndex =
          props.api.getLastDisplayedRowIndex() !== props.rowIndex
            ? props.rowIndex + 1
            : props.rowIndex
      } else {
        nextRowIndex = props.rowIndex
      }
      props.api.stopEditing()
      props.api.clearCellSelection()
      props.api.clearFocusedCell()
      props.api.setFocusedCell(nextRowIndex, props.column)
    }
  }

  const handleBlur = () => {
    // WORKAROUND: close dropdown before stopEditing otherwise it cannot show the new value.
    setOpen(false)
    setTimeout(() => props.api.stopEditing(), 0)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !valueRef.current && !open && !text) {
      const nextRowIndex =
        props.api.getLastDisplayedRowIndex() !== props.rowIndex
          ? props.rowIndex + 1
          : props.rowIndex
      props.api.stopEditing()
      props.api.clearCellSelection()
      props.api.clearFocusedCell()
      props.api.setFocusedCell(nextRowIndex, props.column)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    lastPressedKeyRef.current = event.key
    // When Tab key is pressed, set the selected option as value.
    if (event.key === 'Tab') {
      event.preventDefault()
      const currentValue = props.value
      props.onValueChange(currentValue || undefined)
      props.node.setDataValue(
        props.field || props.uiMeta.externalId,
        currentValue
      )
      updateValue(currentValue || undefined)
      setText(currentValue ? currentValue.name : '')
      props.api.tabToNextCell()
      setOpen(false)
    }
    // When Escape key is pressed, reset the previous option as value.
    if (event.key === 'Escape') {
      props.onValueChange(lastValueRef.current || undefined)
      props.node.setDataValue(
        props.field || props.uiMeta.externalId,
        lastValueRef.current
      )
      updateValue(lastValueRef.current || undefined)
      setText(lastValueRef.current ? lastValueRef.current.name : '')
      const current = props.api.getFocusedCell()
      current &&
        props.api.addCellRange({
          rowStartIndex: current.rowIndex,
          rowEndIndex: current.rowIndex,
          columns: [current.column],
        })
      setOpen(false)
    }
  }

  const getIcon = (
    referenceEntity: string | undefined,
    iconUrl: string | undefined
  ) => {
    if (!referenceEntity || !iconDefined.includes(referenceEntity)) {
      return <></>
    }
    if (!iconUrl) {
      return <NoImageIcon color="action" />
    }
    return <StyledAvatar variant="circular" src={iconUrl} />
  }

  const { uiMeta } = props
  return (
    <RootDiv>
      <Autocomplete
        inputValue={text}
        options={suggestions}
        getOptionLabel={option => (option.name ? option.name : '')}
        renderOption={(renderProps, option: AutocompleteOption) => {
          return (
            <StyledMenuItem {...renderProps}>
              {getIcon(uiMeta.referenceEntity, option.iconUrl)}
              <MenuItemLabel>
                {repositories[uiMeta.referenceEntity!].getLabel(option)}
              </MenuItemLabel>
            </StyledMenuItem>
          )
        }}
        filterOptions={(
          options: AutocompleteOption[],
          _state: FilterOptionsState<AutocompleteOption>
        ) => {
          return options
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <InputBase
            inputRef={inputRef}
            ref={params.InputProps.ref}
            inputProps={params.inputProps}
            autoFocus={true}
          />
        )}
        PopperComponent={popperProps => (
          <Popper {...popperProps} placement="bottom-start" />
        )}
        autoSelect={true}
        blurOnSelect={false}
        onKeyDown={handleKeyDown}
        onKeyPress={handleKeyPress}
        onFocus={handleFocus}
        onInputChange={handleInputChange}
        onChange={handleValueChanged}
        onBlur={handleBlur}
        open={open}
        noOptionsText={''}
      />
    </RootDiv>
  )
}

export default AutocompleteCellEditor
